// @flow
import {
	Bookmarks,
	CalculateOutlined,
	FiberManualRecord,
	InfoOutlined,
	LinkedCamera,
	ShoppingCartOutlined,
	Stars
} from "@mui/icons-material";
import { Divider, Grid, Tooltip, Typography, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useState } from 'react';
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { getTotalProductsInCart } from "../../../helpers/helpers";
import { normalizeGreek } from "../../../helpers/normalizeGreek";
import useOrderAction from "../../../hooks/order/useOrderAction";
import useProductCalculations from "../../../hooks/product/useProductCalculations";
import './product.css'
import useProductQuantity from "../../../hooks/product/useProductQuantity";
import useUserData from "../../../hooks/user/useUserData";
import { PAGE_ORDERS } from "../../../locales/pages/namespaces";
import AvailabilityPopOver from "./partials/availabilityPopOver";
import PricesPopOver from "./partials/pricesPopOver";
import Warehouse from "./partials/warehouse";
import DotsPopOver from "./partials/DotsPopOver";
import DotSelection from "./partials/DotSelection";
import {getRouteUrl} from "../../../helpers/getRouteUrl";
import {ROUTE_PAGE_ORDER_FINALIZE} from "../../../routers/routes";
import {useDotsCache} from "../../../hooks/order/useDotsCache";
import {useHalfCalculatorData} from "../../../hooks/product/useHalfCalculatorData";
import HalfCalculator from "../dialogs/calculator/halfCalculator";

type Props = {};

const Product = (props: Props) => {
	const {
		product: {
			alternativeCode,
			displayAttribute,
			eanCode,
			description,
			id,
			taxPercentage,
			priceWithoutTax,
			priceWithTax,
			priceAfterDiscounts,
			priceAfterDiscountsWithTax,
			priceWithPaymentDiscount,
			priceWithPaymentDiscountWithTax,
			priceWithSecondPaymentDiscount,
			priceWithSecondPaymentDiscountWithTax,
			priceAfterDiscountsWithEnvTax,
			priceAfterDiscountsWithEnvTaxAndItemTax,
			priceWithPaymentDiscountWithEnvTax,
			priceWithPaymentDiscountWithEnvTaxAndItemTax,
			priceWithSecondPaymentDiscountWithEnvTax,
			priceWithSecondPaymentDiscountWithEnvTaxAndItemTax,
			envCharge,
			totalDiscountPercentage,
			totalDiscountValue,
			warehouses,
			oemConstructor,
			oemVehicleType,
			stsCode,
			externalLabelLink,
			manufacturerImage,
			paymentDiscount,
			secondPaymentDiscount
		},
		displayWithTax,
		displayWithEnvTax,
		displayWithPaymentDiscount,
		handleCalculator,
		handleProductInfo
	} = props
	const {t} = useTranslation(PAGE_ORDERS)
	const theme = useTheme();
	const matchesMd = useMediaQuery(theme.breakpoints.down('md'));
	const {thousandSeparator, decimalSeparator, maxAllowedProductsInCart, dateFormat} = useUserData()
	const {createOrder, updateOrder} = useOrderAction()
	const editedWhRef = React.useRef<Set<number>>(new Set());
	const {
		quantity,
		setQuantity,
		activeOrder
	} = useProductQuantity({
		warehouses,
		productId: id,
	})
	const {
		envValue,
		netValue,
		discountValue,
		mappedWarehouses,
		priceWithDisplayPaymentDiscount,
		priceWithDisplayPaymentDiscountWithTax,
		priceWithDisplayPaymentDiscountWithEnvTax,
		priceWithDisplayPaymentDiscountWithEnvTaxAndItemTax
	} = useProductCalculations({
		priceWithoutTax,
		priceWithTax,
		priceAfterDiscounts,
		priceAfterDiscountsWithTax,
		priceWithPaymentDiscount,
		priceWithPaymentDiscountWithTax,
		priceAfterDiscountsWithEnvTax,
		priceAfterDiscountsWithEnvTaxAndItemTax,
		priceWithPaymentDiscountWithEnvTax,
		priceWithPaymentDiscountWithEnvTaxAndItemTax,
		warehouses,
		displayWithTax,
		displayWithEnvTax,
		displayWithPaymentDiscount,
		priceWithSecondPaymentDiscount,
		priceWithSecondPaymentDiscountWithTax,
		priceWithSecondPaymentDiscountWithEnvTax,
		priceWithSecondPaymentDiscountWithEnvTaxAndItemTax,
		paymentDiscount,
		secondPaymentDiscount
	})
	console.log("mappedWarehouses",mappedWarehouses)
	const { halfCalculatorOpen, handleHalfCalculator} = useHalfCalculatorData()
	const [activeWarehouseId, setActiveWarehouseId] = useState<number | null>(null);
	const [openDotDialog,setOpenDotDialog] = useState(false)
	const { getDotsForWh } = useDotsCache(id, warehouses);
	const initialWhRef = React.useRef<Record<string, any>>({});
	const initialDotsByWhRef = React.useRef<Record<string, any[]>>({});
	const sentDotsByWhRef = React.useRef<Record<string, any[]>>({});
	const dirtyDotsWhRef = React.useRef<Set<number>>(new Set());

	const sentDotsByLineRef = React.useRef<Record<string, any[]>>({});
	const dirtyLinesRef = React.useRef<Set<string>>(new Set());
	const [pricesPopOver, setPricesPopOver] = useState({
		anchorEl: null,
		prices: null
	})

	const [dots, setDots] = useState<{
		anchorDotEl: HTMLElement | null;
		warehouseId: number | null;
		dots: any[] | null;
	}>({
		anchorDotEl: null,
		warehouseId: null,
		dots: null,
	});

	const [availability, setAvailability] = useState({
		anchorEl: null,
		warehouse: null
	})
	const [dotSelections, setDotSelections] = useState<Record<number, Record<string, number | ''>>>({});
	const [dotData, setDotData] = useState<any[]>([]);
	const [dotLimits, setDotLimits] = useState<Record<number, number>>({});
	const lineKey = (itemId: number | string, warhId: number | string) =>
		`${String(itemId)}::${String(warhId)}`;

	const dotsToSendFor = (
		itemId: number | string,
		warhId: number | string,
		newDots: any[] | null | undefined,
		rawWDots?: any[],
		getDots?: () => any[]
	) => {
		const key = lineKey(itemId, warhId);
		const isDirty = dirtyLinesRef.current.has(key);

		if (isDirty) {
			return Array.isArray(newDots) ? newDots : [];
		}


		const prev = sentDotsByLineRef.current[key];
		if (Array.isArray(prev) && prev.length > 0) return prev;

		if (Array.isArray(rawWDots) && rawWDots.length > 0) return rawWDots;

		const fetched = getDots ? getDots() : [];
		return Array.isArray(fetched) ? fetched : [];
	};

	const rememberSentDots = (warehouses: any[]) => {
		warehouses?.forEach(w => {
			const idStr = String(w?.id);
			const next = Array.isArray(w?.dots) ? w.dots : [];
			sentDotsByWhRef.current[idStr] = next;
		});

		dirtyDotsWhRef.current.clear();
	};
	const sanitizeDots = (dots: any[]): any[] => {
		if (!Array.isArray(dots)) return [];
		const out: any[] = [];
		for (const d of dots) {
			if (!d || typeof d !== 'object' || Array.isArray(d)) continue;
			const year = String(d.DotYear ?? '');
			const qtyNum = Number(d.Qty);
			if (!year || !Number.isFinite(qtyNum) || qtyNum <= 0) continue;
			out.push({
				DotYear: year,
				Qty: qtyNum, // ΠΑΝΤΑ number
				DiscountPCT: Number.isFinite(Number(d.DiscountPCT)) ? Number(d.DiscountPCT) : 0,
				displayStock: Number.isFinite(Number(d.displayStock)) ? Number(d.displayStock) : 0,
			});
		}
		return out;
	};

	const getPrevWarehousesFromActiveOrder = () => {
		const lines = activeOrder?.[0]?.products ?? activeOrder?.products ?? [];
		const mine = lines.find(
			(p) => String(p.ItemId ?? p.id) === String(id)
		);
		const prev = Array.isArray(mine?.warehouses) ? mine.warehouses : [];

		return prev.map((w) => ({
			...w,
			dots: Array.isArray(w.dots) ? w.dots : [],
		}));
	};

	const rememberFromApiOrder = (apiOrder: any) => {
		if (!apiOrder) return;
		const prods = Array.isArray(apiOrder.products) ? apiOrder.products : [];
		const mine = prods.find((p: any) => String(p.ItemId ?? p.id) === String(id));
		if (!mine) return;
		const itemId = String(mine.ItemId ?? mine.id);
		const whs = Array.isArray(mine.warehouses) ? mine.warehouses : [];
		whs.forEach((w: any) => {
			const key = lineKey(itemId, w.id);
			const dots = Array.isArray(w.dots) ? w.dots : [];
			sentDotsByLineRef.current[key] = JSON.parse(JSON.stringify(dots));
		});
	};

	const mergeWarehousesKeepOld = (prev, updated) => {
		const byId = new Map(prev.map(w => [String(w.id), w]));
		updated.forEach(u => byId.set(String(u.id), u));
		return Array.from(byId.values());
	};

	React.useEffect(() => {
		if (Array.isArray(warehouses) && warehouses.length) {
			const whById: Record<string, any> = {};
			const dotsById: Record<string, any[]> = {};

			warehouses.forEach(w => {
				const id = String(w.id);
				whById[id] = w;
				dotsById[id] = Array.isArray(w.dots) ? [...w.dots] : [];
			});

			if (Object.keys(initialWhRef.current).length === 0) {
				initialWhRef.current = whById;
				initialDotsByWhRef.current = dotsById;
			}
		}
	}, [warehouses]);

	React.useEffect(() => {
		if (!activeOrder) return;
		const prods = Array.isArray(activeOrder.products) ? activeOrder.products : [];
		const mine = prods.find((p: any) => String(p.ItemId ?? p.id) === String(id));
		if (!mine) return;
		const itemId = String(mine.ItemId ?? mine.id);
		const whs = Array.isArray(mine.warehouses) ? mine.warehouses : [];
		whs.forEach((w: any) => {
			const key = lineKey(itemId, w.id);
			const dots = Array.isArray(w.dots) ? w.dots : [];
			sentDotsByLineRef.current[key] = JSON.parse(JSON.stringify(dots));
		});
	}, [activeOrder, id]);

	const handleDots = (next: Partial<typeof dots>) => {
		setDots(prev => ({ ...prev, ...next }));
	};

	const handlePrices = (newValue) => {
		setPricesPopOver({...newValue});
	};
	const handleAvailability = (value) => {
		setAvailability({...value});
	};

	const qtyByWh: Record<string, number> = Object.fromEntries(
		quantity.map(q => [String(q.id), q.quantity || 0])
	);
	const buildWarehousesWithDotSplits = () => {
		const rawWarehouses = Array.isArray(props.product?.warehouses)
			? props.product.warehouses
			: [];

		return rawWarehouses.map((rawW: any) => {
			const idStr = String(rawW.id);
			const userWhQty = qtyByWh[idStr] || 0;

			const base = {
				...rawW,
				quantity: userWhQty,
			};

			const sel = dotSelections?.[Number(idStr)];
			const hasSelectionForThisWh =
				!!sel && Object.values(sel).some((v) => v !== '' && Number(v) > 0);

			const isEdited = editedWhRef.current.has(Number(rawW.id));
			if (isEdited && !hasSelectionForThisWh) {
				return {
					...base,
					dots: [],
				};
			}


			if (!hasSelectionForThisWh) {
				return {
					...base,
					dots: dotsToSendFor(
						id,
						rawW.id,
						[],
						rawW.dots,
						() => getDotsForWh(rawW.id)
					),
				};
			}

			const sourceDots =
				Array.isArray(rawW.dots) && rawW.dots.length > 0
					? rawW.dots
					: getDotsForWh(rawW.id);

			const src = Array.isArray(sourceDots) ? sourceDots : [];
			const newDots: any[] = [];
			for (const d of src) {
				const year = String(d?.DotYear ?? '');
				const qtyNum = Number(sel?.[year]) || 0;
				if (year && qtyNum > 0) {
					newDots.push({
						DotYear: year,
						Qty: qtyNum,
						DiscountPCT: Number(d?.DiscountPCT) || 0,
						displayStock: Number(d?.displayStock) || 0,
					});
				}
			}

			return {
				...base,
				dots: dotsToSendFor(
					id,
					rawW.id,
					newDots,
					rawW.dots,
					() => getDotsForWh(rawW.id)
				),
			};
		});
	};

	const buildWarehousesWithOnlyWhQty = () => {
		const rawWarehouses = Array.isArray(props.product?.warehouses) ? props.product.warehouses : [];

		return rawWarehouses.map((rawW: any) => {
			const idStr = String(rawW.id);
			const userWhQty = qtyByWh[idStr] || 0;
			const isEdited = editedWhRef.current.has(Number(rawW.id));

			return {
				...rawW,
				quantity: userWhQty,
				dots: isEdited
					? []
					: dotsToSendFor(
						id,
						rawW.id,
						[],
						rawW.dots,
						() => getDotsForWh(rawW.id)
					),
			};
		});
	};

	const addToCart = useCallback(() => {

		const qtyByWh: Record<string, number> = Object.fromEntries(
			quantity.map(q => [String(q.id), q.quantity || 0])
		);

		let chosenWithDots = mappedWarehouses
			.map(w => ({ ...w, dots: getDotsForWh(w.id) }))
			.filter(w =>
				(qtyByWh[String(w.id)] || 0) > 0 &&
				Array.isArray(w.dots) && w.dots.length > 0
			);

		if (editedWhRef.current.size > 0) {
			chosenWithDots = chosenWithDots.filter(w => editedWhRef.current.has(w.id));
		}

		if (activeWarehouseId && qtyByWh[String(activeWarehouseId)] > 0) {
			chosenWithDots = chosenWithDots.filter(w => w.id === activeWarehouseId);
		}

		const orderProducts = activeOrder ? activeOrder?.products : [];
		let totalProductQuantity = getTotalProductsInCart(orderProducts, [Number(id)]);
		const productQuantity = quantity.reduce((acc, w) => acc + (w.quantity || 0), 0);
		totalProductQuantity += productQuantity;

		if (totalProductQuantity > maxAllowedProductsInCart) {
			enqueueSnackbar(t('max_allowed_products', { maxAllowedProductsInCart }), {
				variant: 'warning',
				autoHideDuration: 3000
			});
			return;
		}

		if (chosenWithDots.length === 0) {
			const editedWarehouses = buildWarehousesWithOnlyWhQty();
			const prevWarehouses = getPrevWarehousesFromActiveOrder();
			const mergedWarehouses = mergeWarehousesKeepOld(prevWarehouses, editedWarehouses);
			const sanitizedWarehouses = mergedWarehouses.map(w => ({
				...w,
				dots: sanitizeDots(w?.dots),
			}));


			rememberSentDots(sanitizedWarehouses);

			const payload = {
				quantity,
				product: { ...props.product, warehouses: sanitizedWarehouses },
			};
			
			mergedWarehouses.forEach(w => {
				const dots = w?.dots;
				console.log('WarhId:', w?.id, {
					isArray: Array.isArray(dots),
					length: Array.isArray(dots) ? dots.length : 'n/a',
					types: Array.isArray(dots) ? dots.map(d => (d === null ? 'null' : Array.isArray(d) ? 'array' : typeof d)) : 'n/a',
					hasNull: Array.isArray(dots) ? dots.some(d => d === null) : 'n/a',
					sample: Array.isArray(dots) ? dots.slice(0, 3) : dots,
				});
			});

			const result =
				activeOrder
					? updateOrder({ ...payload, orderId: activeOrder.id })
					: createOrder(payload);

			Promise.resolve(result)
				.then((res) => {
					if (res?.order?.[0]) {
						rememberFromApiOrder(res.order[0]);
					}
				})
				.catch((err) => {
					console.error('[update/create] failed:', err);
				})
				.finally(() => {
					dirtyLinesRef.current.clear();
				});


			return;
		}



		const limits: Record<number, number> = {};
		chosenWithDots.forEach(w => { limits[w.id] = qtyByWh[w.id] || 0; });

		setDotData(chosenWithDots);
		setDotLimits(limits);
		setDotSelections(Object.fromEntries(chosenWithDots.map(w => [w.id, {}])));
		setOpenDotDialog(true);
	}, [activeOrder,
		createOrder, id,
		maxAllowedProductsInCart,
		props.product, quantity, t, updateOrder, mappedWarehouses, warehouses]);

	return (
		<>
			<Grid container sx={{
				background: '#FFFFFF', padding: 0.4, marginBottom: 1,
				border: '1px solid rgba(37,91,130, 0.5)',
				boxShadow: 'rgba(37,91,130, 0.4) 0px 1px 2px 0px, rgba(37,91,130, 0.2) 0px 1px 3px 1px'
			}}>
				<Grid item xs={12}>
					<Box
						sx={{
							display: "flex",
							width: "100%",
							flexDirection: 'row',
							flexWrap: 'wrap',
							justifyContent: 'space-between',
							alignItems: 'center',
							pt: 0.1
						}}
					>
							<img src={manufacturerImage} alt={'logo'} style={{maxHeight: "40px", maxWidth: '120px'}}/>
						<Typography
							component={'div'}
							variant={'overline'}
						>
							<strong>{displayAttribute && normalizeGreek(displayAttribute)} </strong>
						</Typography>
					</Box>
					<Divider/>
				</Grid>
				<Grid
					item
					xs={12}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							flexWrap: 'wrap',
							width: '100%',
						}}
					>
						<Box
							sx={{
								width: {
									xs: '100%',
									sm: '50%',
									md: '15%',
									lg: '25%',
									xl: '20%'
								},
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'space-between'
							}}
						
						>
							<Box>
								<Typography variant={'h5'} component={'div'} sx={{wordWrap: 'break-word'}}>
									{description}
								</Typography>
								<Typography variant={'body2'} component={'div'}>
									{t('EAN CODE')}: {eanCode}
								</Typography>
								<Typography variant={'body2'} component={'div'}>
									{t('Alternative Code')}: {alternativeCode}
								</Typography>
								{stsCode && (
									<Typography variant={'body2'} component={'div'}>
										{t('ERP Code')}: {stsCode}
									</Typography>
								)}
								<Box>
									<Tooltip title={t('Product images and info')}>
										<Button
											variant="text"
											sx={{
												padding: 0,
												minWidth: 30,
												maxHeight: 30,
												marginLeft: 0.1,
												marginRight: 0.1
											}}
											onClick={() => handleProductInfo({
												open: true,
												product: {...props.product}
											})}
										>
											<LinkedCamera fontSize={'small'}/>
										</Button>
									</Tooltip>
									<Tooltip title={t('EPREL - European Product Registry for Energy Labelling')}>
										<a
											href={externalLabelLink}
											target={'_blank'}
											rel="noreferrer"
										>
											<Button
												variant="text"
												sx={{
													padding: 0,
													minWidth: 30,
													maxHeight: 30,
													marginLeft: 0.1,
													marginRight: 0.1
												}}
											
											>
												<Bookmarks fontSize={'small'} color={'secondary'}/>
											</Button>
										</a>
									</Tooltip>
								</Box>
								{oemConstructor && oemVehicleType && (
									<>
										<Typography variant={'body2'} component={'div'}>
											<strong>OEM</strong>
										</Typography>
										<Typography variant={'body2'} component={'div'}>
											{t('OEM Constructor')}: {oemConstructor}
										</Typography>
										<Typography variant={'body2'} component={'div'}>
											{t('OEM Vehicle Type')}: {oemVehicleType}
										</Typography>
									</>
								)}
							</Box>
						</Box>
						<Box
							sx={{
								width: {
									xs: '100%',
									sm: '100%',
									md: '75%',
									lg: '60%',
									xl: '60%'
								},
								p: 0.2,
								...(matchesMd && { order: 2 })
							}}
							className={'warehouses'}
						>
							<Box className={'warehouse'}>
								<Box
									pl={0.4}
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'flex-start',
										wordWrap: 'break-word',
										mb: 0.5, // μικρό κενό πριν τις γραμμές
									}}
								>
									<Box pr={0.1} sx={{ width: { xs: 80, md: 72, lg: 115 }, flexShrink: 0 }} />
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: {
												xs: '38px 16px 16px 28px 28px minmax(70px, 1fr)',
												md: '34px 16px 16px 24px 24px minmax(82px, 1fr)',
												lg: '50px 16px 21px 40px 40px minmax(90px, 1fr)'
											},
											alignItems: 'center',
											flex: 1,
											gap: { xs: 0.4, md: 0.15, lg: 0.15 },
											minWidth: 0,
											color: 'text.secondary',
										}}
									>
										<Box
											sx={{
												gridColumn: 3,
												lineHeight: 1,
												textAlign: 'center'
											}}
										>
											<Typography
												variant="caption"
												sx={{
													fontSize: 10,
													fontWeight: 'bold',
													color: 'primary.main',
													whiteSpace: 'nowrap'
											}}
											>
												{normalizeGreek(t('DOT'))}
											</Typography>
										</Box>
										<Box sx={{ gridColumn: 6 }} />
									</Box>
									<Box sx={{ width: { xs: 72, lg: 80 }, flexShrink: 0 }} />
								</Box>

								{mappedWarehouses.map((warehouse, index) => (
									<Warehouse
										productId={id}
										key={warehouse.id}
										warehouse={warehouse}
										onQuantityChange={(whId: number, nextQty: number) => {
											const set = editedWhRef.current;
											if ((nextQty || 0) > 0) set.add(whId);
											else set.delete(whId);
										}}
										index={index}
										handleAvailability={handleAvailability}
										handleDots={handleDots}
										warehouses={warehouses}
										quantity={quantity}
										setQuantity={setQuantity}
										activeOrder={activeOrder}
										dateFormat={dateFormat}
										getDotsForWh={getDotsForWh}
									/>
								))}
							</Box>
							<Box className={'warehouses-cart'}>
								<Tooltip title={t('Add to cart')}>
									<Button
										 onClick={addToCart}
									>
										<ShoppingCartOutlined/>
									</Button>
								</Tooltip>
							</Box>
						</Box>
						<Box
							sx={{
								width: {
									xs: '100%',
									sm: '50%',
									md: '20%',
									lg: '25%',
									xl: '20%'
								},
								position: 'relative'
							}}
						>
							<Box sx={{
								position: 'absolute',
								right: 0
							}}>
								<Tooltip title={'Υπολογισμοί'}>
									<Button
										variant={'text'}
										sx={{minHeight: 0, minWidth: 0, padding: 0}}
										onClick={() => handleCalculator({
											open: true,
											prices: {
												taxPercentage,
												priceWithoutTax,
												priceWithTax,
												priceAfterDiscounts,
												priceAfterDiscountsWithTax,
												priceWithPaymentDiscount: priceWithDisplayPaymentDiscount,
												priceWithPaymentDiscountWithTax: priceWithDisplayPaymentDiscountWithTax,
												priceAfterDiscountsWithEnvTax,
												priceAfterDiscountsWithEnvTaxAndItemTax,
												priceWithPaymentDiscountWithEnvTax: priceWithDisplayPaymentDiscountWithEnvTax,
												priceWithPaymentDiscountWithEnvTaxAndItemTax: priceWithDisplayPaymentDiscountWithEnvTaxAndItemTax,
												envCharge: envCharge.value,
												description: description
											}
										})}
									>
										<CalculateOutlined fontSize={'large'} p={0}/>
									</Button>
								</Tooltip>
							</Box>
							<Grid container pt={1}>
								<Grid item xs={12}>
									<InfoOutlined
										color={'primary'}
										sx={{width: '16px', height: '16px'}}
										onMouseEnter={(evt) => handlePrices({
											anchorEl: evt.currentTarget,
											prices: {
												taxPercentage,
												priceWithoutTax,
												priceWithTax,
												priceAfterDiscounts,
												priceAfterDiscountsWithTax,
												priceWithPaymentDiscount: priceWithDisplayPaymentDiscount,
												priceWithPaymentDiscountWithTax: priceWithDisplayPaymentDiscountWithTax,
												priceAfterDiscountsWithEnvTax,
												priceAfterDiscountsWithEnvTaxAndItemTax,
												priceWithPaymentDiscountWithEnvTax: priceWithDisplayPaymentDiscountWithEnvTax,
												priceWithPaymentDiscountWithEnvTaxAndItemTax: priceWithDisplayPaymentDiscountWithEnvTaxAndItemTax,
												envCharge: envCharge.value
											}
										})}
										onMouseLeave={(evt) => handlePrices({
											anchorEl: null,
											prices: null
										})}
									/>
								</Grid>
								<Grid item xs={8}>
									<Typography component={'div'} variant={'body2'}>
										{t('Wholesale Value')}
									</Typography>
								</Grid>
								<Grid item xs={4}>
									<NumericFormat
										thousandSeparator={thousandSeparator}
										decimalSeparator={decimalSeparator}
										displayType="text"
										value={netValue}
									/> €
								</Grid>
								<Grid item xs={8}>
									<Typography component={'div'} variant={'body2'}>
										{t('Invoice Value')}
									</Typography>
								</Grid>
								<Grid item xs={4}>
									<NumericFormat
										thousandSeparator={thousandSeparator}
										decimalSeparator={decimalSeparator}
										displayType="text"
										value={discountValue}
									/> €
								</Grid>
								<Grid item xs={8}>
									<Typography component={'div'} variant={'body2'}>
										{t('Final Cost (with payment discount)')}
									</Typography>
								</Grid>
								<Grid item xs={4}>
									<NumericFormat
										thousandSeparator={thousandSeparator}
										decimalSeparator={decimalSeparator}
										displayType="text"
										value={envValue}
									/> €
								</Grid>
								<Grid item>
									<Box>
										{totalDiscountValue > 0 && (
											<Typography variant={'caption'} component={'div'}
														sx={{display: 'flex', alignItems: 'center'}}>
												<Stars color={'error'}
													   sx={{width: '0.75rem', height: '0.75rem', mr: 0.1}}/>
												<strong>{t('Discount (per piece)')}</strong>
												<span style={{paddingLeft: '4px'}}>{` - `}</span>
												<NumericFormat
													thousandSeparator={thousandSeparator}
													decimalSeparator={decimalSeparator}
													displayType="text"
													value={totalDiscountValue}
												/> €
											</Typography>
										)}
										{totalDiscountPercentage > 0 && (
											<Typography variant={'caption'} component={'div'}
														sx={{display: 'flex', alignItems: 'center'}}>
												<FiberManualRecord color={'secondary'}
																   sx={{width: '0.75rem', height: '0.75rem', mr: 0.1}}/>
												<strong>{t('Discount (per piece)')}</strong>
												<span style={{paddingLeft: '4px'}}>{` - `}</span>
												<NumericFormat
													thousandSeparator={thousandSeparator}
													decimalSeparator={decimalSeparator}
													displayType="text"
													value={totalDiscountPercentage}
												/> %
											</Typography>
										)}
									</Box>
								</Grid>
							</Grid>
						</Box>
					</Box>
				</Grid>
			</Grid>
			<DotSelection
				halfCalculatorOpen={halfCalculatorOpen}
				handleHalfCalculator={(next) =>
					handleHalfCalculator(
						next?.open
							? {
								open: true,
								prices: {
									taxPercentage,
									priceWithoutTax,
									priceWithTax,
									priceAfterDiscounts,
									priceAfterDiscountsWithTax,
									priceWithPaymentDiscount: priceWithDisplayPaymentDiscount,
									priceWithPaymentDiscountWithTax: priceWithDisplayPaymentDiscountWithTax,
									priceAfterDiscountsWithEnvTax,
									priceAfterDiscountsWithEnvTaxAndItemTax,
									priceWithPaymentDiscountWithEnvTax: priceWithDisplayPaymentDiscountWithEnvTax,
									priceWithPaymentDiscountWithEnvTaxAndItemTax:
									priceWithDisplayPaymentDiscountWithEnvTaxAndItemTax,
									envCharge: envCharge.value,
									description,
								},
							}
							: { open: false, prices: null }
					)
				}
				prices={{
					taxPercentage,
					priceWithoutTax,
					priceWithTax,
					priceAfterDiscounts,
					priceAfterDiscountsWithTax,
					priceWithPaymentDiscount: priceWithDisplayPaymentDiscount,
					priceWithPaymentDiscountWithTax: priceWithDisplayPaymentDiscountWithTax,
					priceAfterDiscountsWithEnvTax,
					priceAfterDiscountsWithEnvTaxAndItemTax,
					priceWithPaymentDiscountWithEnvTax: priceWithDisplayPaymentDiscountWithEnvTax,
					priceWithPaymentDiscountWithEnvTaxAndItemTax:
					priceWithDisplayPaymentDiscountWithEnvTaxAndItemTax,
					envCharge: envCharge.value,
					description,
				}}
				open={openDotDialog}
				data={dotData}
				qtyLimits={dotLimits}
				dotsState={dotSelections}
				setDotsState={setDotSelections}
				handleDialog={setOpenDotDialog}
				title={t('Select Dots')}
				itemId={id}
				markDotDirty={(itemId: number|string, warhId: number|string) => {
					dirtyLinesRef.current.add(lineKey(itemId, warhId));
				}}
				onYes={async () => {
					const hasAnyDotQty = Object.values(dotSelections || {}).some(
						(perWh) => perWh && Object.values(perWh).some((q) => q !== '' && Number(q) > 0)
					);

					const exceeds = dotData?.some((w) => {
						const sel = dotSelections?.[w.id] || {};
						const sumSel = Object.values(sel).reduce((acc, v) => acc + (Number(v) || 0), 0);
						return sumSel > (qtyByWh[String(w.id)] || 0);
					});
					if (exceeds) {
						enqueueSnackbar(t('DOT quantity is bigger than warehouse quantity.'), { variant: 'warning' });
						return;
					}

					const mismatch = dotData?.some((w) => {
						const sel = dotSelections?.[w.id] || {};
						const sumSel = Object.values(sel).reduce((acc, v) => acc + (Number(v) || 0), 0);
						const limit = qtyByWh[String(w.id)] || 0;
						return sumSel !== limit;
					});

					if (mismatch) {
						enqueueSnackbar(
							t('Selected DOT quantity must exactly match requested warehouse quantity.'),
							{ variant: 'warning' }
						);
						return;
					}
					setOpenDotDialog(false);
					const warehousesForPayload = hasAnyDotQty
						? buildWarehousesWithDotSplits()
						: buildWarehousesWithOnlyWhQty();

					const prevWarehouses = getPrevWarehousesFromActiveOrder();
					const mergedWarehouses = mergeWarehousesKeepOld(prevWarehouses, warehousesForPayload);

					const sanitizedWarehouses = mergedWarehouses.map(w => ({
						...w,
						dots: sanitizeDots(w?.dots),
					}));


					rememberSentDots(sanitizedWarehouses);

					const payload = {
						quantity,
						product: { ...props.product, warehouses: sanitizedWarehouses},
					};

					mergedWarehouses.forEach(w => {
						const dots = w?.dots;
						console.log('WarhId:', w?.id, {
							isArray: Array.isArray(dots),
							length: Array.isArray(dots) ? dots.length : 'n/a',
							types: Array.isArray(dots) ? dots.map(d => (d === null ? 'null' : Array.isArray(d) ? 'array' : typeof d)) : 'n/a',
							hasNull: Array.isArray(dots) ? dots.some(d => d === null) : 'n/a',
							sample: Array.isArray(dots) ? dots.slice(0, 3) : dots,
						});
					});



					const res = activeOrder
						? await updateOrder({ ...payload, orderId: activeOrder.id })
						: await createOrder(payload);

					rememberFromApiOrder(res?.order?.[0]);
					dirtyLinesRef.current.clear();
					editedWhRef.current.clear();
				}}
				onNo={async () => {
					setOpenDotDialog(false);

					const editedWarehouses = buildWarehousesWithOnlyWhQty();
					const prevWarehouses = getPrevWarehousesFromActiveOrder();
					const mergedWarehouses = mergeWarehousesKeepOld(prevWarehouses, editedWarehouses);
					const sanitizedWarehouses = mergedWarehouses.map(w => ({
						...w,
						dots: sanitizeDots(w?.dots),
					}));


					rememberSentDots(sanitizedWarehouses);

					const payload = {
						quantity,
						product: { ...props.product, warehouses: sanitizedWarehouses },
					};


					const res = activeOrder
						? await updateOrder({ ...payload, orderId: activeOrder.id })
						: await createOrder(payload);

					rememberFromApiOrder(res?.order?.[0]);
					editedWhRef.current.clear();
					dirtyLinesRef.current.clear();
				}}
				yesBtnText={t('Add to cart')}
				noBtnText={t('Continue to cart without selection')}
				hasBtnIcons={false}
			/>


			<AvailabilityPopOver
				availability={availability}
				handleAvailability={handleAvailability}
			/>
			<DotsPopOver
				dots={dots}
			handleDots={handleDots}
			/>
			<PricesPopOver
				open={pricesPopOver}
				handlePrices={handlePrices}
			/>
		</>
	);
};

export default Product